/**
 * Load data for a screen.
 *
 * Deliberately small: the app has no client-side cache because almost every screen shows
 * something that changes on the server (a run's status, a build's progress), and a stale
 * cached value there is worse than a fetch.
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, signIn } from "@/api/client";

export interface AsyncState<T> {
  data: T | undefined;
  error: ApiError | Error | undefined;
  loading: boolean;
  /** Re-run the loader, e.g. after a mutation. */
  reload: () => void;
}

interface Internal<T> {
  data: T | undefined;
  error: ApiError | Error | undefined;
  loading: boolean;
}

export function useAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  options: { pollMs?: number; enabled?: boolean } = {},
): AsyncState<T> {
  const { pollMs, enabled = true } = options;

  // One piece of state, so a load never produces two renders in a row.
  const [state, setState] = useState<Internal<T>>({
    data: undefined,
    error: undefined,
    loading: enabled,
  });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    let cancelled = false;

    const run = async (isPoll: boolean) => {
      if (!isPoll) {
        setState((current) => ({ ...current, loading: true }));
      }
      try {
        const result = await loader(controller.signal);
        if (cancelled) return;
        setState({ data: result, error: undefined, loading: false });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.isUnauthenticated) {
          // The session expired mid-session; send them back through sign-in rather than
          // showing an error they cannot act on.
          signIn();
          return;
        }
        setState((current) => ({ ...current, error: caught as Error, loading: false }));
      }
    };

    void run(false);

    const timer = pollMs ? window.setInterval(() => void run(true), pollMs) : undefined;

    return () => {
      cancelled = true;
      controller.abort();
      if (timer) window.clearInterval(timer);
    };
    // `loader` is deliberately excluded: callers pass an inline closure, so including it
    // would restart the effect on every render. `deps` is the caller's statement of what
    // the load actually depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, enabled, pollMs]);

  return {
    data: state.data,
    error: state.error,
    // A disabled loader is never loading, whatever the last state said.
    loading: enabled && state.loading,
    reload,
  };
}
